/**
 * Parse URLs from a text string.
 * Extracts unique, normalized URLs with validation.
 *
 * Supports:
 * - http / https URLs
 * - www.* URLs (assumes https)
 *
 * @param text - Text containing URLs
 * @returns Array of unique URLs
 */
export function parseReferencesUrls(text: string): string[] {
  if (!text || text.trim() === '') {
    return []
  }

  const urlRegex = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi

  const matches = (text.match(urlRegex) ?? []).map(trimTrailingPunctuation)

  const normalizedUrls = matches
    .map((rawUrl) => normalizeUrl(rawUrl))
    .filter((url): url is string => url !== null)

  return Array.from(new Set(normalizedUrls))
}

/**
 * Normalize a single URL the same way `parseReferencesUrls` normalizes the
 * URLs it extracts: upgrade bare `www.*` to https, lowercase the host, strip
 * a trailing slash. Returns null for input that can't be parsed as a URL.
 */
export function normalizeUrl(rawUrl: string): string | null {
  if (!rawUrl) return null
  try {
    const withProtocol = rawUrl.startsWith('www.')
      ? `https://${rawUrl}`
      : rawUrl

    const url = new URL(withProtocol)

    let normalized = url.toString()
    if (normalized.endsWith('/')) {
      normalized = normalized.slice(0, -1)
    }

    return normalized
  } catch {
    return null
  }
}

/**
 * Strip trailing sentence punctuation and unbalanced closing brackets from a
 * URL match. Balanced brackets inside the URL are preserved.
 */
function trimTrailingPunctuation(url: string): string {
  const pairs: Record<string, string> = { ')': '(', ']': '[', '}': '{' }
  let out = url
  while (out.length > 0) {
    const last = out[out.length - 1]
    if ('.,;:!?'.includes(last)) {
      out = out.slice(0, -1)
      continue
    }
    const opener = pairs[last]
    if (opener) {
      const opens = out.split(opener).length - 1
      const closes = out.split(last).length - 1
      if (closes > opens) {
        out = out.slice(0, -1)
        continue
      }
    }
    break
  }
  return out
}
